import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mail, Check, Inbox } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [filter, setFilter] = useState("new");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/inquiries", { params: filter === "all" ? {} : { status: filter } });
    setInquiries(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const mark = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success(status === "handled" ? "Yanıtlandı olarak işaretlendi" : "Tekrar yeni yapıldı");
      await load();
    } catch {
      toast.error("Güncellenemedi");
    }
  };

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex items-center gap-3">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-earth-400/20 text-earth-500">
          <Inbox className="h-6 w-6" />
        </span>
        <div>
          <h1 className="font-display text-5xl text-ink-900" data-testid="inquiries-title">
            Ürün talepleri
          </h1>
          <p className="font-body text-ink-700">Manuel olarak sen yönetiyorsun. E-posta ile ulaşman yeterli.</p>
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        {[
          { k: "new", label: "Yeni" },
          { k: "handled", label: "Yanıtlanmış" },
          { k: "all", label: "Tümü" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setFilter(t.k)}
            data-testid={`inquiries-filter-${t.k}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
              filter === t.k
                ? "border-sage-500 bg-sage-500 text-cream-50"
                : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-3">
        {loading && [1, 2].map((i) => <div key={i} className="k-card h-28 animate-pulse" />)}
        {!loading && inquiries.length === 0 && (
          <div className="k-card p-10 text-center font-body text-ink-500" data-testid="inquiries-empty">
            Şu an gösterilecek talep yok.
          </div>
        )}
        {!loading &&
          inquiries.map((iq) => (
            <div
              key={iq.inquiry_id}
              className="k-card flex flex-col gap-4 p-6 md:flex-row md:items-start md:justify-between"
              data-testid={`inquiry-${iq.inquiry_id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs text-ink-500">
                  <span
                    className={`rounded-full px-2 py-0.5 font-bold ${
                      iq.status === "new" ? "bg-brick-500/20 text-brick-600" : "bg-sage-500/20 text-sage-700"
                    }`}
                  >
                    {iq.status === "new" ? "Yeni" : "Yanıtlanmış"}
                  </span>
                  <span>{formatDate(iq.created_at)}</span>
                </div>
                <h3 className="mt-2 font-heading text-lg font-bold text-ink-900">{iq.product_title}</h3>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-700">
                  <span className="inline-flex items-center gap-1">
                    <Mail className="h-4 w-4" />{" "}
                    <a href={`mailto:${iq.email}`} className="text-sage-700 hover:underline">
                      {iq.email}
                    </a>
                  </span>
                  {iq.name && <span>· {iq.name}</span>}
                </div>
                {iq.message && (
                  <p className="mt-3 whitespace-pre-wrap rounded-xl bg-cream-200/50 p-3 font-body text-sm text-ink-900">
                    {iq.message}
                  </p>
                )}
              </div>
              <div className="flex gap-2 md:flex-col">
                {iq.status === "new" ? (
                  <button
                    onClick={() => mark(iq.inquiry_id, "handled")}
                    className="k-btn-secondary text-sm py-2 px-4"
                    data-testid={`mark-handled-${iq.inquiry_id}`}
                  >
                    <Check className="mr-1 h-4 w-4" /> Yanıtlandı
                  </button>
                ) : (
                  <button
                    onClick={() => mark(iq.inquiry_id, "new")}
                    className="k-btn-ghost text-sm"
                    data-testid={`mark-new-${iq.inquiry_id}`}
                  >
                    Tekrar yeni yap
                  </button>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}
